import { Button, Divider, Form, Input, Row, Space } from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'

import { Box } from 'atom/box'
import { InputWrapper } from 'atom/chargers'
import { renderFormatMessage } from 'helpers/intl'

import { Tenant, TenantPayload } from 'models/tenant'

import { useTenantTabPlatform } from 'hooks/useTenantTabPlatform'

import { theme } from 'theme'
import { useGeneralTranslation } from 'hooks/translation/useGeneralTranslation'
import { ReusableSelector } from 'components/reusableSelector/selector'
import { useSelector } from 'hooks/useSelector'
import { FindPartners } from 'services/data-provider/partners'

interface TenantTabDetailProps {
  tenant: Tenant
  onSave: (tenant: TenantPayload) => void
}

export const TenantTabPartnerIntegration: React.FC<TenantTabDetailProps> = ({ tenant, onSave }) => {
  const {
    form,
    disabled,
    isDuplicate,
    isMockUpEnabled,
    changedIndexes,
    handleFormPlatformSubmit,
    handleToggleDisabled,
    handlePartnerOptionChange,
    setDeletedNames,
  } = useTenantTabPlatform(tenant, onSave)

  const initialValues = {
    tenant_partners: tenant.tenantPartners?.map(
      ({ partnerId, partnerName, integrationIdentifier, isIntegrationIdentifierPartnerProvided }) => ({
        partnerId,
        partnerName,
        integrationIdentifier,
        isIntegrationIdentifierPartnerProvided,
      }),
    ),
  }

  const { modifyText, cancelText, saveText } = useGeneralTranslation()

  const { generalDebounceFetcher, handleSingleSelectChange, renderSingleOption } = useSelector()

  // Custom form submission handler with validation
  const handleSubmitWithValidation = async (values: any) => {
    // Validate integration identifiers
    const partners = values.tenant_partners || []
    for (let i = 0; i < partners.length; i++) {
      const integrationIdentifier = partners[i]?.integrationIdentifier

      // Check if empty
      if (!integrationIdentifier || integrationIdentifier.trim() === '') {
        form.setFields([
          {
            name: ['tenant_partners', i, 'integrationIdentifier'],
            errors: ['Integration identifier cannot be empty'],
          },
        ])
        return // Stop submission
      }

      // Check for special characters (only letters, numbers, and hyphens allowed)
      if (!/^[a-zA-Z0-9-]*$/.test(integrationIdentifier)) {
        form.setFields([
          {
            name: ['tenant_partners', i, 'integrationIdentifier'],
            errors: ['Cannot have special characters'],
          },
        ])
        return // Stop submission
      }
    }

    handleFormPlatformSubmit(values)
  }

  // Function to handle Generate ID button click
  const handleGenerateId = (name: number) => {
    // Get current form values to access partner name
    const currentValues = form.getFieldsValue()
    const currentPartners = currentValues.tenant_partners || []
    const partnerName = currentPartners[name]?.partnerName || 'partner'
    // Clean partner name (remove spaces, special chars, convert to lowercase)
    const cleanPartnerName = partnerName.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
    const now = new Date()
    const year = now.getUTCFullYear()
    const month = String(now.getUTCMonth() + 1).padStart(2, '0')
    const day = String(now.getUTCDate()).padStart(2, '0')
    const hours = String(now.getUTCHours()).padStart(2, '0')
    const minutes = String(now.getUTCMinutes()).padStart(2, '0')
    const seconds = String(now.getUTCSeconds()).padStart(2, '0')
    const uuid = `${year}${month}${day}${cleanPartnerName}${hours}${minutes}${seconds}`

    // Update the form with the new values using simplified approach
    const formValues = form.getFieldsValue()
    const updatedPartners = [...(formValues.tenant_partners || [])]
    if (updatedPartners[name]) {
      updatedPartners[name] = {
        ...updatedPartners[name],
        integrationIdentifier: uuid,
        isIntegrationIdentifierPartnerProvided: false,
      }
    }
    form.setFieldsValue({
      tenant_partners: updatedPartners,
    })
  }

  return (
    <Box padding>
      <Row align="middle" justify="center">
        <Form
          form={form}
          requiredMark={false}
          name={`tenant-${tenant?.id}`}
          onFinish={handleSubmitWithValidation}
          initialValues={initialValues}
        >
          <InputWrapper>
            <Form.List name="tenant_partners">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key}>
                      <Space align="baseline" style={{ display: 'flex' }} wrap>
                        <Form.Item hidden {...restField} name={[name, 'partnerId']} label="Partner ID">
                          <Input placeholder="Partner ID" disabled={disabled} />
                        </Form.Item>
                        <Form.Item
                          hidden
                          {...restField}
                          name={[name, 'isIntegrationIdentifierPartnerProvided']}
                          label="Is Integration Identifier Partner Provided"
                        >
                          <Input placeholder="Is Integration Identifier Partner Provided" disabled={disabled} />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          className="align-center"
                          name={[name, 'partnerName']}
                          label="Platform Name"
                          rules={[{ required: true, message: 'Please select a platform' }]}
                          style={{ marginBottom: 0 }}
                        >
                          <ReusableSelector
                            showSearch
                            disabled={disabled}
                            onOptionsChange={handlePartnerOptionChange(name)}
                            defaultValues={
                              !changedIndexes.includes(name) && initialValues?.tenant_partners?.[name]
                                ? [initialValues?.tenant_partners?.[name]?.partnerName]
                                : undefined
                            }
                            dropDownList={
                              !changedIndexes.includes(name) && initialValues?.tenant_partners?.[name]
                                ? [initialValues?.tenant_partners?.[name]]
                                : []
                            }
                            isSingle
                            allowClear={false}
                            clearSearchAfterSelect
                            noDefaultSearchText
                            oneTimeUpdateDefaultValues
                            placeholder={'Select a platform'}
                            showMagnifySVG={false}
                            maxTagCount={0}
                            customWidth="170px"
                            isDebounceFetcher={true}
                            debounceFetcher={generalDebounceFetcher(
                              (value) => FindPartners(isMockUpEnabled, value),
                              true,
                              'data',
                              1,
                            )}
                            handleOptionChange={handleSingleSelectChange('partnerName')}
                            renderOption={renderSingleOption(
                              {
                                val: 'partnerName',
                                label: 'partnerName',
                              },
                              false,
                            )}
                            isMockUpEnabled={isMockUpEnabled}
                          />
                        </Form.Item>

                        <Form.Item
                          shouldUpdate={(prevValues, curValues) =>
                            prevValues.tenant_partners?.[name]?.partnerName !==
                            curValues.tenant_partners?.[name]?.partnerName
                          }
                          noStyle
                        >
                          {() => {
                            return (
                              <Form.Item
                                {...restField}
                                className="align-center"
                                name={[name, 'integrationIdentifier']}
                                label={renderFormatMessage(
                                  'dashboard.text.integrationIdentifier',
                                  'Integration Identifier',
                                )}
                              >
                                <Input
                                  disabled={disabled}
                                  style={{ width: '300px' }}
                                  onChange={(e) => {
                                    const currentValues = form.getFieldsValue()
                                    const updatedPartners = [...(currentValues.tenant_partners || [])]
                                    if (updatedPartners[name]) {
                                      updatedPartners[name] = {
                                        ...updatedPartners[name],
                                        isIntegrationIdentifierPartnerProvided: true,
                                      }
                                    }
                                    form.setFieldsValue({
                                      tenant_partners: updatedPartners,
                                    })
                                  }}
                                />
                              </Form.Item>
                            )
                          }}
                        </Form.Item>

                        <Form.Item
                          shouldUpdate={(prevValues, curValues) =>
                            prevValues.tenant_partners?.[name]?.partnerName !==
                            curValues.tenant_partners?.[name]?.partnerName
                          }
                          noStyle
                        >
                          {() => {
                            const currentValues = form.getFieldsValue()
                            const currentPartners = currentValues.tenant_partners || []
                            const partnerName = currentPartners[name]?.partnerName || ''

                            // Hide button if: greystar partner
                            if (partnerName.toLowerCase() === 'greystar') {
                              return null
                            }

                            return (
                              <Form.Item>
                                <Button
                                  type="default"
                                  size="small"
                                  disabled={disabled}
                                  onClick={() => handleGenerateId(name)}
                                >
                                  Generate ID
                                </Button>
                              </Form.Item>
                            )
                          }}
                        </Form.Item>
                        <Form.Item>
                          <DeleteOutlined
                            onClick={(e) => {
                              if (disabled) {
                                e.stopPropagation() // Prevents click events when disabled
                              } else {
                                remove(name)
                                setDeletedNames(name)
                              }
                            }}
                            style={{
                              fontSize: theme.fontSizes[2],
                              color: disabled ? theme.colors.grey4 : 'inherit', // Grayed out if disabled
                              cursor: disabled ? 'not-allowed' : 'pointer',
                            }}
                          />
                        </Form.Item>
                      </Space>
                      <Divider style={{ margin: '6px 0 24px 0' }} />
                    </div>
                  ))}

                  <Form.Item>
                    <Button
                      type="primary"
                      disabled={disabled}
                      icon={<PlusOutlined />}
                      onClick={() =>
                        add({
                          partnerId: '',
                          partnerName: '',
                          integrationIdentifier: '',
                          isIntegrationIdentifierPartnerProvided: true,
                        })
                      }
                    >
                      {renderFormatMessage('dashboard.text.addPlatform', 'Add Platform')}
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </InputWrapper>

          <Form.Item>
            <Row justify="center">
              <Space>
                <Button htmlType="submit" type="primary" disabled={disabled || isDuplicate}>
                  {saveText}
                </Button>
                {disabled ? (
                  <Button htmlType="button" onClick={handleToggleDisabled} disabled={!disabled} type="primary">
                    {modifyText}
                  </Button>
                ) : (
                  <Button
                    danger
                    htmlType="button"
                    onClick={() => {
                      handleToggleDisabled()
                      form.resetFields()
                    }}
                  >
                    {cancelText}
                  </Button>
                )}
              </Space>
            </Row>
          </Form.Item>
        </Form>
      </Row>
    </Box>
  )
}
